// ======================================================
// FILE: GoodsReceivedWeighBridgeTicketList.js
// PURPOSE (SAFE UPDATE):
// - Keep everything working as-is
// - Add "Create Physical GRN" + "Create Virtual GRN" buttons on the page (NOT sidebar)
// - Reuse existing permissions check (CREATE_GOODS_RECEIVED_WEIGH_BRIDGE_TICKET)
// - Keep existing "Add GRN" modal button unchanged
// ======================================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import Head from "../../../layout/head/Head";
import Content from "../../../layout/content/Content";
import { useSelector } from "react-redux";
import * as app_consts from "../../../constants/react_app_consts";
import { useDispatch } from "react-redux";
import {
  fail_retrieve_goods_received_weigh_bridge_tickets,
  loading_retrieve_goods_received_weigh_bridge_tickets,
  reset_retrieve_goods_received_weigh_bridge_tickets,
  success_retrieve_goods_received_weigh_bridge_tickets,
} from "../../../reducers/dashboard_reducers/warehouse/retrieve_goods_received_weigh_bridge_tickets_Slice";
import {
  RETRIEVE_GOODS_RECEIVED_WEIGH_BRIDGE_TICKET_ACCESS_DENIED_MSG,
  RETRIEVE_GOODS_RECEIVED_WEIGH_BRIDGE_TICKET_ERR_MSG,
  RETRIEVE_GOODS_RECEIVED_WEIGH_BRIDGE_TICKET_SUCCESS_MSG,
} from "../../../constants/success_error_messages_consts";
import {
  RETRIEVE_GOODS_RECEIVED_WEIGH_BRIDGE_TICKET_LIST_API,
} from "../../../api/api_data";
import { messageToast } from "../../../utils/Utils";
import {
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  DataTableBody,
  DataTableHead,
  DataTableItem,
  DataTableRow,
  Icon,
  PaginationComponent,
} from "../../../components/Component";
import { capitalizeSentences, findModuleAction, formatToSentenceCase } from "../../../utils/functionalUtils";
import { employee_dashboard_actions_helper } from "../../../actions/actions_helper";
import { BiErrorCircle } from "react-icons/bi";
import { Button, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from "reactstrap";
import moment from "moment";
import { Link, useNavigate } from "react-router-dom";
import AuthLoader from "../../components/page_messaging/AuthLoader";
import AuthError from "../../components/page_messaging/AuthError";
import AuthNoData from "../../components/page_messaging/AuthNoData";
import ModalAdd from "../components/modal/modal_add/ModalAdd";

const initial_actions_data = {
  create_goods_received_weigh_bridge_ticket: false,
};
const type = app_consts.GOODS_RECEIVED_WEIGH_BRIDGE_TICKET_LIST;

const GOODS_RECEIVED_LIST_STATE_KEY = "goods_received_list_state";
const PRODUCT_PREVIEW_LIMIT = 3;
const STATUS_COLORS = {
  success: "#1f9d55",
  warning: "#d69e2e",
  info: "#3182ce",
  danger: "#e53e3e",
  secondary: "#718096",
};

const getGoodsReceivedStatusMeta = (item = {}) => {
  const rawStatus =
    item?.weigh_bridge_ticket_status?.weigh_bridge_ticket_status ||
    item?.weigh_bridge_ticket?.weigh_bridge_ticket_status ||
    "";
  const statusId = Number(
    item?.weigh_bridge_ticket_status?.weigh_bridge_ticket_status_id ??
      item?.weigh_bridge_ticket?.weigh_bridge_ticket_status_id
  );
  const normalizedStatus = String(rawStatus || "").trim().toLowerCase();

  if (statusId === app_consts.WEIGH_BRIDGE_TICKET_PROCESSING_COMPLETE || normalizedStatus.includes("complete")) {
    return { color: "success", label: "Processing Complete", rawStatus };
  }

  if (normalizedStatus === String(app_consts.IN_TRANSIT || "").toLowerCase()) {
    return { color: "info", label: "In Transit", rawStatus };
  }

  if (normalizedStatus === String(app_consts.OFFLOADING_GOODS || "").toLowerCase()) {
    return { color: "warning", label: "Offloading Goods", rawStatus };
  }

  if (normalizedStatus === String(app_consts.PROCESSCING_INBOUND || "").toLowerCase()) {
    return { color: "warning", label: "Processing Inbound", rawStatus };
  }

  if (normalizedStatus.includes("cancel")) {
    return { color: "danger", label: "Cancelled", rawStatus };
  }

  return { color: "secondary", label: rawStatus || "Unknown", rawStatus };
};

const GOODS_RECEIVED_STATUS_LEGEND = [
  { color: "info", label: "In Transit" },
  { color: "warning", label: "Offloading Goods" },
  { color: "warning", label: "Processing Inbound" },
  { color: "success", label: "Processing Complete" },
  { color: "danger", label: "Cancelled" },
];

const firstNonEmptyProductList = (...candidates) => {
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate;
    }
  }

  return [];
};

const getGoodsReceivedProducts = (item = {}) =>
  firstNonEmptyProductList(
    item?.goods_received_products,
    item?.grn_products,
    item?.product_list,
    item?.goods_received?.goods_received_products
  );

const getGoodsReceivedProductNames = (item = {}) =>
  [
    ...new Set(
      getGoodsReceivedProducts(item)
        .map(
          (product) =>
            product?.product_name ||
            product?.product_label ||
            product?.product?.product_name ||
            product?.product ||
            product?.name ||
            ""
        )
        .filter(Boolean)
    ),
  ];

const GoodsReceivedWeighBridgeTicketList = () => {
  const [page_data, setPageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const dispatch = useDispatch();
  const [componentData, setComponentData] = useState(initial_actions_data);
  const [sm, updateSm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage] = useState(10);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const [add_modal, setAddModal] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("All");
  const navigate = useNavigate();
  const restoredPageRef = useRef(null);

  const shouldIgnoreRowClick = (target) => {
    if (!target || typeof target.closest !== "function") return false;

    return Boolean(
      target.closest("a, button, input, select, textarea, label, .nk-tb-col-tools, .dropdown-toggle, .dropdown-menu")
    );
  };

  // Restore saved state on mount
  useEffect(() => {
    const savedState = localStorage.getItem(GOODS_RECEIVED_LIST_STATE_KEY);
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        restoredPageRef.current = parsed.currentPage || 1;
        setCurrentPage(parsed.currentPage || 1);
        setSelectedFilter(parsed.selectedFilter || "All");
        setSearchTerm(parsed.searchTerm || "");
      } catch (e) {
        console.error("Failed to parse saved state", e);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      GOODS_RECEIVED_LIST_STATE_KEY,
      JSON.stringify({
        currentPage,
        selectedFilter,
        searchTerm,
        timestamp: Date.now(),
      })
    );
  }, [currentPage, selectedFilter, searchTerm]);

  const handleNavigateToDetails = (refNumber) => {
    navigate(`/dashboard/goods_received/${refNumber}`);
  };

  const handleRowNavigation = (event, refNumber) => {
    if (!refNumber || shouldIgnoreRowClick(event.target)) return;
    handleNavigateToDetails(refNumber);
  };

  useEffect(() => {
    retrieve_goods_received_weigh_bridge_tickets_func();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh List Function
  const refreshList = () => {
    setError("");
    retrieve_goods_received_weigh_bridge_tickets_func();
    setPageData([]);
  };

  // Retrieve GRN List
  const retrieve_goods_received_weigh_bridge_tickets_func = () => {
    dispatch(
      employee_dashboard_actions_helper(
        loading_retrieve_goods_received_weigh_bridge_tickets,
        success_retrieve_goods_received_weigh_bridge_tickets,
        fail_retrieve_goods_received_weigh_bridge_tickets,
        RETRIEVE_GOODS_RECEIVED_WEIGH_BRIDGE_TICKET_ERR_MSG,
        RETRIEVE_GOODS_RECEIVED_WEIGH_BRIDGE_TICKET_SUCCESS_MSG,
        app_consts.GET,
        RETRIEVE_GOODS_RECEIVED_WEIGH_BRIDGE_TICKET_LIST_API,
        null,
        RETRIEVE_GOODS_RECEIVED_WEIGH_BRIDGE_TICKET_ACCESS_DENIED_MSG,
      ),
    );
  };

  // Weigh Bridge Ticket List Listener
  const goods_received_weigh_bridge_tickets = useSelector(
    (state) => state.retrieve_goods_received_weigh_bridge_tickets.retrieve_goods_received_weigh_bridge_tickets,
  );

  useEffect(() => {
    if (goods_received_weigh_bridge_tickets.status !== app_consts.IDLE) {
      if (goods_received_weigh_bridge_tickets.status === app_consts.LOADING) {
        setError("");
        setLoading(true);
      } else {
        if (goods_received_weigh_bridge_tickets.status === app_consts.FAIL) {
          setLoading(false);
          setError(goods_received_weigh_bridge_tickets.message || RETRIEVE_GOODS_RECEIVED_WEIGH_BRIDGE_TICKET_ERR_MSG);
          messageToast(RETRIEVE_GOODS_RECEIVED_WEIGH_BRIDGE_TICKET_ERR_MSG, app_consts.FAIL);
        } else if (goods_received_weigh_bridge_tickets.status === app_consts.SUCCESS) {
          setPageData(goods_received_weigh_bridge_tickets.data);
          setLoading(false);
        }
        dispatch(reset_retrieve_goods_received_weigh_bridge_tickets());
      }
    }
  }, [goods_received_weigh_bridge_tickets, dispatch]);

  // Retrieve Module Actions Data
  const action_roles = useSelector((state) => state.auth_routes.auth_routes);

  useEffect(() => {
    if (action_roles.status === app_consts.SUCCESS) {
      if (action_roles.data) {
        const warehouse_management = action_roles?.data?.find(
          (module) => module.module_name === app_consts.WAREHOUSE_MANAGEMENT,
        );
        setComponentData(warehouse_management);
      }
    }
  }, [action_roles]);

  const open_add_modal = () => setAddModal(true);
  const close_add_modal = () => setAddModal(false);

  const successAddFunction = () => close_add_modal();

  const filterGRNs = (value) => {
    setSelectedFilter(value);
    setCurrentPage(1);
  };

  const current_page_data = useMemo(() => {
    let filtered = page_data;

    if (selectedFilter === "Physical Goods Received") {
      filtered = filtered?.filter(
        (grn) =>
          grn?.weigh_bridge_ticket?.weigh_bridge_ticket_status?.toLowerCase() !== app_consts.IN_TRANSIT?.toLowerCase(),
      );
    } else if (selectedFilter === "In Transit Goods Received") {
      filtered = filtered?.filter(
        (grn) =>
          grn?.weigh_bridge_ticket?.weigh_bridge_ticket_status?.toLowerCase() === app_consts.IN_TRANSIT?.toLowerCase(),
      );
    }

    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();

      filtered = filtered?.filter((item) => {
        return (
          item?.goods_received?.goods_received_ref_number?.toLowerCase().includes(lowerSearch) ||
          item?.goods_received?.grn_origin?.toLowerCase().includes(lowerSearch) ||
          item?.consigner?.name?.toLowerCase().includes(lowerSearch) ||
          item?.consigner?.unique_identification_number?.toLowerCase().includes(lowerSearch) ||
          item?.weigh_bridge_ticket?.truck_licence_plate?.toLowerCase().includes(lowerSearch) ||
          item?.weigh_bridge_ticket?.trailer_one?.toLowerCase().includes(lowerSearch) ||
          item?.weigh_bridge_ticket?.trailer_two?.toLowerCase().includes(lowerSearch) ||
          item?.sum_grn_products?.toString().toLowerCase().includes(lowerSearch) ||
          (item?.weigh_bridge_ticket?.time_in &&
            moment(item?.weigh_bridge_ticket?.time_in)
              .format("YYYY-MM-DD h:mm:ss")
              .toLowerCase()
              .includes(lowerSearch)) ||
          item?.weigh_bridge_ticket?.weigh_bridge_ticket_status?.toLowerCase().includes(lowerSearch)
        );
      });
    }

    return filtered || [];
  }, [page_data, searchTerm, selectedFilter]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(current_page_data.length / itemPerPage));

    if (restoredPageRef.current !== null) {
      setCurrentPage(Math.min(restoredPageRef.current, maxPage));
      restoredPageRef.current = null;
      return;
    }

    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [current_page_data.length, itemPerPage, currentPage]);

  const indexOfLastItem = currentPage * itemPerPage;
  const indexOfFirstItem = indexOfLastItem - itemPerPage;
  const currentItems = current_page_data?.slice(indexOfFirstItem, indexOfLastItem);

  // ======================================================
  // SAFE: permission gate for create buttons
  // ======================================================
  const canCreateGRN = findModuleAction(app_consts.CREATE_GOODS_RECEIVED_WEIGH_BRIDGE_TICKET, componentData) !== null;

  return (
    <>
      <Head title="Goods Received" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Goods Received</BlockTitle>
              <BlockDes className="text-soft">
                <p>{!(loading || error) && ` Goods Received ${current_page_data?.length}`}</p>
              </BlockDes>

              {!loading && (
                <div className="mt-2 d-flex gap-2 flex-wrap">
                  {/* Refresh */}
                  <Link
                    onClick={(e) => {
                      e.preventDefault();
                      refreshList();
                    }}
                  >
                    <Button color="light" outline className="bg-white d-none d-sm-inline-flex">
                      <span>Refresh List</span>
                    </Button>
                  </Link>
                </div>
              )}
            </BlockHeadContent>

            {!(loading || error) && (
              <BlockHeadContent>
                <div className="toggle-wrap nk-block-tools-toggle">
                  <a
                    href="#more"
                    className="btn btn-icon btn-trigger toggle-expand me-n1"
                    onClick={(ev) => {
                      ev.preventDefault();
                      updateSm(!sm);
                    }}
                  >
                    <Icon name="more-v"></Icon>
                  </a>
                  <div className="toggle-expand-content" style={{ display: sm ? "block" : "none" }}>
                    <ul className="nk-block-tools g-3">
                      {canCreateGRN && (
                        <li className="nk-block-tools-opt">
                          <UncontrolledDropdown>
                            <DropdownToggle color="primary" className="btn d-inline-flex align-items-center">
                              <Icon name="plus"></Icon>
                              <span className="ms-2">Goods Received Actions</span>
                              <Icon name="chevron-down" className="ms-2" />
                            </DropdownToggle>
                            <DropdownMenu end className="mt-1">
                              <DropdownItem onClick={open_add_modal}>Add GRN</DropdownItem>
                              <DropdownItem
                                tag={Link}
                                to="/dashboard/goods_received/create_physical_goods_received_ticket"
                              >
                                Weighbridge GRN
                              </DropdownItem>
                            </DropdownMenu>
                          </UncontrolledDropdown>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </BlockHeadContent>
            )}
          </BlockBetween>
        </BlockHead>

        {loading ? (
          <AuthLoader loading_message="Loading Goods Received..." />
        ) : (
          <>
            {error ? (
              <AuthError error_message={error} />
            ) : (
              <>
                {page_data && page_data?.length > 0 ? (
                  <>
                    <ul className="nk-block-tools g-3 d-flex justify-end">
                      <li>
                        <div className="">
                          <div className="form-icon form-icon-right">
                            <Icon name="search"></Icon>
                          </div>
                          <input
                            type="text"
                            className="form-control"
                            id="default-04"
                            value={searchTerm}
                            onChange={(e) => {
                              setSearchTerm(e.target.value);
                              setCurrentPage(1);
                            }}
                            placeholder="Search for Goods Received"
                          />
                        </div>
                      </li>
                      <li className="preview-item">
                        <UncontrolledDropdown>
                          <div className="btn-group">
                            <DropdownToggle color="light" outline className="dropdown-toggle-split btn-white">
                              <Icon name="chevron-down"></Icon>
                              <span>{selectedFilter}</span>
                            </DropdownToggle>
                          </div>
                          <DropdownMenu className="mt-3">
                            <ul className="link-list-opt">
                              <li>
                                <DropdownItem
                                  tag="a"
                                  href="#"
                                  onClick={(ev) => {
                                    ev.preventDefault();
                                    filterGRNs("All");
                                  }}
                                >
                                  <span>All</span>
                                </DropdownItem>
                              </li>
                              <li>
                                <DropdownItem
                                  tag="a"
                                  href="#"
                                  onClick={(ev) => {
                                    ev.preventDefault();
                                    filterGRNs("Physical Goods Received");
                                  }}
                                >
                                  <span>Physical Goods Received</span>
                                </DropdownItem>
                              </li>
                              <li>
                                <DropdownItem
                                  tag="a"
                                  href="#"
                                  onClick={(ev) => {
                                    ev.preventDefault();
                                    filterGRNs("In Transit Goods Received");
                                  }}
                                >
                                  <span>In Transit Goods Received</span>
                                </DropdownItem>
                              </li>
                            </ul>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </li>
                    </ul>

                    <div className="d-flex flex-wrap align-items-center gap-2 mb-3 px-1">
                      <span className="text-soft small">Status legend:</span>
                      {GOODS_RECEIVED_STATUS_LEGEND.map((statusItem) => (
                        <div key={statusItem.label} className="d-inline-flex align-items-center gap-1 me-2">
                          <span
                            style={{
                              width: "12px",
                              height: "12px",
                              borderRadius: "999px",
                              display: "inline-block",
                              backgroundColor: STATUS_COLORS[statusItem.color] || STATUS_COLORS.secondary,
                            }}
                          />
                          <span className="small text-soft">{statusItem.label}</span>
                        </div>
                      ))}
                    </div>

                    <div className="nk-tb-list is-separate is-medium mt-1 mb-3">
                      <div className="card-inner p-0">
                        <DataTableBody>
                          <DataTableHead>
                            <DataTableRow size="sm">
                              <span>Goods Received Ref No.</span>
                            </DataTableRow>
                            <DataTableRow>
                              <span>Client</span>
                            </DataTableRow>
                            <DataTableRow>
                              <span>Horse</span>
                            </DataTableRow>
                            <DataTableRow>
                              <span>Trailer</span>
                            </DataTableRow>
                            <DataTableRow>
                              <span>Product</span>
                            </DataTableRow>
                            <DataTableRow>
                              <span>Total Tonnage (MT)</span>
                            </DataTableRow>
                            <DataTableRow size="md">
                              <span> Date Received</span>
                            </DataTableRow>
                            <DataTableRow size="md">
                              <span>Status</span>
                            </DataTableRow>
                            <DataTableRow size="md">
                              <span>Action</span>
                            </DataTableRow>
                          </DataTableHead>

                          {currentItems?.length > 0
                            ? currentItems.map((item) => {
                                const goodsReceivedRefNumber = item?.goods_received?.goods_received_ref_number;
                                const statusMeta = getGoodsReceivedStatusMeta(item);
                                const productNames = getGoodsReceivedProductNames(item);
                                return (
                                  <DataTableItem
                                    key={goodsReceivedRefNumber}
                                    className="cursor-pointer"
                                    role="link"
                                    tabIndex={0}
                                    onClick={(event) => handleRowNavigation(event, goodsReceivedRefNumber)}
                                    onKeyDown={(event) => {
                                      if (event.key !== "Enter" && event.key !== " ") return;
                                      event.preventDefault();
                                      handleRowNavigation(event, goodsReceivedRefNumber);
                                    }}
                                  >
                                    <DataTableRow size="sm">
                                      <Link to={`/dashboard/goods_received/${goodsReceivedRefNumber}`}>
                                        <span className="tb-product">
                                          <span className="title">{goodsReceivedRefNumber || "--"}</span>
                                        </span>
                                        <div>
                                          <span className="fw-medium">Origin: </span>
                                          <span className="tb-sub">
                                            {item?.goods_received?.grn_origin
                                              ? formatToSentenceCase(item?.goods_received?.grn_origin)
                                              : "--"}
                                          </span>
                                        </div>
                                      </Link>
                                    </DataTableRow>

                                    <DataTableRow>
                                      <span className="tb-sub">
                                        {item?.consigner?.name
                                          ? item?.consigner?.name.length > 20
                                            ? `${item?.consigner?.name.substring(0, 20)}...`
                                            : capitalizeSentences(item?.consigner?.name)
                                          : "---"}
                                      </span>
                                      <div>
                                        <span className="fw-medium">ID: </span>
                                        <span className="tb-sub">
                                          {item?.consigner?.unique_identification_number || "--"}
                                        </span>
                                      </div>
                                    </DataTableRow>

                                    <DataTableRow>
                                      <span className="tb-sub">
                                        {item?.weigh_bridge_ticket?.truck_licence_plate || "--"}
                                      </span>
                                    </DataTableRow>

                                    <DataTableRow>
                                      <div>
                                        <span className="fw-medium">Trailer 1: </span>
                                        <span className="tb-sub">{item?.weigh_bridge_ticket?.trailer_one || "--"}</span>
                                      </div>
                                      <div>
                                        <span className="fw-medium">Trailer 2: </span>
                                        <span className="tb-sub">{item?.weigh_bridge_ticket?.trailer_two || "--"}</span>
                                      </div>
                                    </DataTableRow>

                                    <DataTableRow>
                                      <span className="tb-sub">
                                        {productNames.length > 0
                                          ? productNames.slice(0, PRODUCT_PREVIEW_LIMIT).map((productName, idx) => (
                                              <span key={productName}>
                                                {productName}
                                                {idx < Math.min(productNames.length, PRODUCT_PREVIEW_LIMIT) - 1 ? ", " : ""}
                                              </span>
                                            ))
                                          : "--"}
                                        {productNames.length > PRODUCT_PREVIEW_LIMIT
                                          ? ` +${productNames.length - PRODUCT_PREVIEW_LIMIT} more`
                                          : ""}
                                      </span>
                                    </DataTableRow>

                                    <DataTableRow>
                                      <span className="tb-sub">{item?.sum_grn_products || "--"}</span>
                                    </DataTableRow>

                                    <DataTableRow>
                                      <span className="tb-sub">
                                        {item?.weigh_bridge_ticket?.time_in
                                          ? moment(item?.weigh_bridge_ticket?.time_in).format("YYYY-MM-DD h:mm:ss")
                                          : "--"}
                                      </span>
                                    </DataTableRow>

                                    <DataTableRow size="md">
                                      <span className="tb-odr-status">
                                        <span
                                          title={statusMeta.label}
                                          style={{
                                            width: "18px",
                                            height: "18px",
                                            borderRadius: "999px",
                                            display: "inline-block",
                                            backgroundColor: STATUS_COLORS[statusMeta.color] || STATUS_COLORS.secondary,
                                          }}
                                        />
                                      </span>
                                    </DataTableRow>

                                    <DataTableRow className="nk-tb-col-tools">
                                      <div className="tb-odr-btns d-none d-sm-inline">
                                        <Link to={`/dashboard/goods_received/${goodsReceivedRefNumber}`}>
                                          <Button color="primary" size="sm" className="btn btn-dim">
                                            View
                                          </Button>
                                        </Link>
                                      </div>
                                      <Button className="btn-pd-auto d-sm-none">
                                        <Icon name="chevron-right"></Icon>
                                      </Button>
                                    </DataTableRow>
                                  </DataTableItem>
                                );
                              })
                            : null}
                        </DataTableBody>

                        {currentItems?.length > 0 && (
                          <div className="mt-3">
                            <PaginationComponent
                              itemPerPage={itemPerPage}
                              totalItems={current_page_data?.length}
                              paginate={paginate}
                              currentPage={currentPage}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <AuthNoData no_data_message={"No Goods Received Data Found."} />
                )}

                <ModalAdd
                  openModal={add_modal}
                  setOpenModal={setAddModal}
                  modalTitle="Add Goods Recieved"
                  modalDescription="Please Complete this form to Create a Goods Received Token"
                  type={type}
                  succFunc={successAddFunction}
                  size="xl"
                />
              </>
            )}
          </>
        )}
      </Content>
    </>
  );
};

export default GoodsReceivedWeighBridgeTicketList;
